import React, { useState } from "react"
import { Link } from "react-router-dom"
import checkIcon from "../../assets/icons/check_icon.svg"
import pricingData from "./pricingSection.json"

const { header, pricing, features, scaleRows, expertConsultation, cta, ctaSubtext } = pricingData
const { firstPlaybookPrice, additionalPlaybookPrice, calculatorLabel, maxPlaybooks } = pricing

function calcPrice(count) {
  return count === 1 ? firstPlaybookPrice : firstPlaybookPrice + (count - 1) * additionalPlaybookPrice
}

function getBreakdown(count) {
  if (count === 1) return `1 playbook × $${firstPlaybookPrice}`
  return `$${firstPlaybookPrice} + ${count - 1} × $${additionalPlaybookPrice} additional`
}

function getActiveRow(count) {
  return scaleRows.reduce((best, t) => (count >= t ? t : best), scaleRows[0])
}

export const PricingSection = () => {
  const [count, setCount] = useState(1)

  const price = calcPrice(count)
  const breakdown = getBreakdown(count)
  const activeRow = getActiveRow(count)

  return (
    <section className="section3" id="pricing">
      <div className="section-container">
        {/* ── Header ─────────────────────────────── */}
        <div className="pricing-header">
          <p className="pricing-eyebrow">{header.eyebrow}</p>
          <h1 className="our-pricing-section-title">
            {header.title.split("\n").map((line, i, lines) => (
              <React.Fragment key={i}>
                {line}
                {i < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </h1>
          <p className="pricing-subtitle">{header.subtitle}</p>
        </div>

        {/* ── Calculator Card ─────────────────────── */}
        <div className="pricing-calculator-card">
          {/* Stepper */}
          <div className="pricing-calculator">
            <p className="pricing-calculator-label">{calculatorLabel}</p>
            <div className="pricing-stepper">
              <button
                type="button"
                className="stepper-btn"
                onClick={() => setCount(c => Math.max(1, c - 1))}
                disabled={count <= 1}
                aria-label="Decrease playbook count"
              >
                −
              </button>
              <span className="stepper-count">{count}</span>
              <button
                type="button"
                className="stepper-btn"
                onClick={() => setCount(c => Math.min(maxPlaybooks, c + 1))}
                disabled={count >= maxPlaybooks}
                aria-label="Increase playbook count"
              >
                +
              </button>
            </div>
          </div>

          {/* Price display */}
          <div className="pricing-price-display">
            <span className="pricing-amount">${price.toLocaleString()}</span>
            <span className="pricing-period">per month</span>
            <span className="pricing-breakdown">{breakdown}</span>
          </div>

          <hr className="pricing-divider" />

          {/* Features */}
          <p className="pricing-features-title">Everything included</p>
          <div className="pricing-features-grid">
            {features.map((feature, i) => (
              <div className="pricing-feature-item" key={i}>
                <div className="pricing-feature-check">
                  <img src={checkIcon} alt="" />
                </div>
                <span className="pricing-feature-text">{feature}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Link to="/contact" className="pricing-cta-btn">
            {cta}
          </Link>
          <p className="pricing-cta-subtext">{ctaSubtext}</p>
        </div>

        {/* ── Scale Table ─────────────────────────── */}
        <div className="pricing-scale-table">
          <p className="pricing-scale-title">How it scales</p>
          {scaleRows.map((n, i) => (
            <React.Fragment key={n}>
              <div className={`pricing-scale-row${activeRow === n ? " active" : ""}`}>
                <span className="scale-label">
                  {n} {n === 1 ? "playbook" : "playbooks"}
                </span>
                <span className="scale-price">
                  ${calcPrice(n).toLocaleString()} <span className="scale-per">/ mo</span>
                </span>
              </div>
              {i < scaleRows.length - 1 && <div className="scale-row-divider" />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Expert Consultation ─────────────────── */}
        <div className="pricing-expert-row">
          <div>
            <p className="expert-label">{expertConsultation.label}</p>
            <p className="expert-sub">{expertConsultation.description}</p>
          </div>
          <div className="expert-price">
            ${expertConsultation.pricePerHour}
            <span className="expert-per"> / hour</span>
          </div>
        </div>
      </div>
    </section>
  )
}
